/**
 * Regulile partenerului, o singura data — folosite de API la salvare si de interfata la
 * completare. Ca la `telefon.ts`: doua implementari care diverg ar produce exact problema pe care
 * tabela comuna de parteneri o rezolva.
 *
 * Design: docs/superpowers/specs/2026-10-15-parteneri-comuni-new-partner-design.md (§4).
 * Reguli stabilite de Alexandru (15.10.2026):
 *  - persoana fizica: cod fiscal `Tel.` + telefon, Nr. Reg. Com. `X`, tara implicit Romania;
 *  - firma din alta tara: judet `-Strainatate-` (obligatoriu), Nr. Reg. Com. `J`;
 *  - asociatie, fundatie (nu e la Registrul Comertului): Nr. Reg. Com. `J`, bifat in fereastra
 *    (Dorian, 30.10.2026);
 *  - Bucuresti: judet „Municipiul Bucuresti", localitate obligatoriu „Sector 1"…„Sector 6";
 *  - obligatorii: denumire, cod fiscal, Nr. Reg. Com., tara, judet, localitate, strada si numar.
 */
import { codTelefonEa, numarNationalRo } from './telefon.js';
/** Valorile EXACTE din lista EA `fields.partners.state`, plus judetul firmelor straine. */
export const JUDETE_EA = [
    'Alba',
    'Arad',
    'Arges',
    'Bacau',
    'Bihor',
    'Bistrita-Nasaud',
    'Botosani',
    'Braila',
    'Brasov',
    'Bucuresti',
    'Buzau',
    'Calarasi',
    'Caras-Severin',
    'Cluj',
    'Constanta',
    'Covasna',
    'Dambovita',
    'Dolj',
    'Galati',
    'Giurgiu',
    'Gorj',
    'Harghita',
    'Hunedoara',
    'Ialomita',
    'Iasi',
    'Ilfov',
    'Maramures',
    'Mehedinti',
    'Municipiul Bucuresti',
    'Mures',
    'Neamt',
    'Olt',
    'Prahova',
    'Salaj',
    'Satu-Mare',
    'Sibiu',
    'Suceava',
    'Teleorman',
    'Timis',
    'Tulcea',
    'Valcea',
    'Vaslui',
    'Vrancea',
];
/** Judetul unei firme din afara Romaniei, in EA. */
export const JUDET_STRAINATATE = '-Strainatate-';
export const MUNICIPIUL_BUCURESTI = 'Municipiul Bucuresti';
export const SECTOARE_BUCURESTI = [
    'Sector 1',
    'Sector 2',
    'Sector 3',
    'Sector 4',
    'Sector 5',
    'Sector 6',
];
/** Cheie de potrivire: MAJUSCULE, fara diacritice, fara spatii/cratime/punctuatie. */
function cheieJudet(s) {
    return s
        .toUpperCase()
        .replace(/[ȘŞ]/g, 'S')
        .replace(/[ȚŢ]/g, 'T')
        .replace(/[ĂÂ]/g, 'A')
        .replace(/Î/g, 'I')
        .replace(/[^A-Z0-9]/g, '');
}
const JUDET_DUPA_CHEIE = new Map();
for (const j of JUDETE_EA)
    JUDET_DUPA_CHEIE.set(cheieJudet(j), j);
// ANAF da „BUCURESTI"; forma EA folosita la verificarea ANAF e „Municipiul Bucuresti".
JUDET_DUPA_CHEIE.set(cheieJudet('Bucuresti'), MUNICIPIUL_BUCURESTI);
JUDET_DUPA_CHEIE.set(cheieJudet(JUDET_STRAINATATE), JUDET_STRAINATATE);
/**
 * Judetul (din ANAF / CRM / tastat) → forma exacta EA. Nerecunoscut → originalul, trimmed (EA il
 * poate accepta sau semnala; nu pierdem date).
 */
export function judetEa(input) {
    const t = (input ?? '').trim();
    if (!t)
        return null;
    return JUDET_DUPA_CHEIE.get(cheieJudet(t)) ?? t;
}
export function eBucuresti(judet) {
    return judetEa(judet) === MUNICIPIUL_BUCURESTI;
}
/** „sector 3", „Sectorul 3", „S3" → „Sector 3". Altfel null. */
export function sectorBucuresti(localitate) {
    const t = localitate ?? '';
    // „Sector 3", „S3", si forma ANAF „Bucuresti Sectorul 3" / „Mun. Bucuresti, sector 3".
    const m = t.match(/^\s*s(?:ector(?:ul)?)?\s*\.?\s*([1-6])\s*$/i) ??
        t.match(/\bsector(?:ul)?\s*([1-6])\b/i);
    return m ? `Sector ${m[1]}` : null;
}
export function eRomania(tara) {
    const t = cheieJudet(tara ?? '');
    return t === '' || t === 'ROMANIA' || t === 'RO';
}
/**
 * Codul fiscal normalizat — cheia de unicitate si de potrivire. `RO123` = `123`; persoana fizica
 * `TEL.` + cifre; `ID.…` (forma veche din EA) cum e; firma straina cu prefixul ei de tara; ce nu e
 * cod (gol, o adresa de email) → null.
 */
export function cheieCodFiscal(cif) {
    const c = (cif ?? '').trim();
    if (!c || c.includes('@'))
        return null;
    if (/^tel\./i.test(c)) {
        const d = c.replace(/\D/g, '');
        return d ? `TEL.${d}` : null;
    }
    if (/^id\./i.test(c))
        return c.toUpperCase();
    if (/^[a-z]{2}/i.test(c) && !/^ro/i.test(c))
        return c.toUpperCase().replace(/\s+/g, '');
    const d = c.replace(/\D/g, '');
    return d || null;
}
const curat = (v) => (v ?? '').trim() || null;
export function pregatestePartener(d, opt = {}) {
    const erori = {};
    const fizica = d.tip === 'fizica';
    const tara = curat(d.tara) ?? 'Romania';
    const strain = !fizica && !eRomania(tara);
    const denumire = curat(d.denumire);
    if (!denumire)
        erori.denumire = 'Denumirea e obligatorie.';
    let codFiscal = '';
    if (fizica) {
        const cod = codTelefonEa(d.telefon);
        if (!cod)
            erori.telefon =
                'La persoană fizică, telefonul (10 cifre) e obligatoriu — din el se face codul fiscal.';
        else
            codFiscal = cod;
    }
    else if (strain) {
        codFiscal = (d.codFiscal ?? '').trim().toUpperCase().replace(/\s+/g, '');
        if (!codFiscal && !opt.codOptionalStrain)
            erori.codFiscal = 'Codul fiscal (VAT) e obligatoriu.';
    }
    else {
        const cifre = (d.codFiscal ?? '').replace(/\D/g, '');
        if (!/^\d{2,10}$/.test(cifre))
            erori.codFiscal = 'Cod fiscal invalid: 2–10 cifre, cu sau fără RO.';
        // Platitor de TVA → `RO`. Necunoscut (fara ANAF) → cum a fost tastat.
        else
            codFiscal = (d.platitorTva ?? /^\s*ro/i.test(d.codFiscal ?? '')) ? `RO${cifre}` : cifre;
    }
    // Nr. Reg. Com.: X la persoana fizica, J la firma straina — completate de regula, nu de om.
    const regCom = fizica ? 'X' : strain ? 'J' : (curat(d.regCom) ?? '');
    if (!regCom)
        erori.regCom =
            'Nr. Reg. Com. e obligatoriu (la asociații și fundații, care nu sunt la Registrul Comerțului: J).';
    const judet = strain ? JUDET_STRAINATATE : (judetEa(d.judet) ?? '');
    if (!judet)
        erori.judet = 'Județul e obligatoriu.';
    let localitate = curat(d.localitate) ?? '';
    if (!strain && eBucuresti(judet)) {
        const sector = sectorBucuresti(localitate);
        if (!sector)
            erori.localitate = 'În București, localitatea e sectorul: Sector 1 … Sector 6.';
        else
            localitate = sector;
    }
    else if (!localitate)
        erori.localitate = 'Localitatea e obligatorie.';
    const adresa = curat(d.adresa);
    if (!adresa)
        erori.adresa = 'Strada și numărul sunt obligatorii.';
    else if (!strain && !/\d/.test(adresa) && !/f[aă]r[aă]\s*num[aă]r|f\/n|fn\b/i.test(adresa))
        erori.adresa = 'Adresa trebuie să aibă și numărul (sau „fără număr").';
    if (d.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim()))
        erori.email = 'Email invalid.';
    if (Object.keys(erori).length)
        return { ok: false, erori };
    return {
        ok: true,
        date: {
            tip: d.tip,
            denumire: denumire,
            codFiscal,
            codFiscalCheie: cheieCodFiscal(codFiscal),
            platitorTva: fizica || strain ? null : codFiscal.startsWith('RO'),
            regCom,
            tara,
            judet,
            localitate,
            adresa: adresa,
            codPostal: curat(d.codPostal),
            telefon: numarNationalRo(d.telefon) ?? curat(d.telefon),
            email: curat(d.email),
            persoanaContact: curat(d.persoanaContact),
            iban: curat(d.iban)?.replace(/\s+/g, '').toUpperCase() ?? null,
            strain,
        },
    };
}
//# sourceMappingURL=parteneri.js.map